import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AnalyticsSiteAssets,
  DataLayerAnalytics,
  setAdobeAnalytics,
  User
} from '@com-delta-idp/mnx-skymiles-landing-page-ui';
import * as OverviewInterfaces from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  Summary,
  SummaryAnalyticsEvent
} from '@com-delta-idp/mnx-skymiles-shared';
import { CookieHelperService } from '@com-delta-idp/mnx-utils';
import { Subject } from 'rxjs';
import {
  AUTHENTICATED_USER,
  DATA_ANALYTICS,
  DataLayerPageLoad
} from '../../models/analytics/data-layer.constants';
import { ErrorDetails } from '../../models/api/content-api.interfaces';
import { getModalClickAttributes } from '../../utils/analytics-helper.utils';
import { getPageInfoDataForAnalytics } from '../../utils/analytics.utils';
import {
  setLinkClickAnalytics as setLinkClickAnalyticsUtil,
  setupBannerClickListeners,
  updateSiteAssets as updateSiteAssetsUtil
} from '../../utils/banner-click-listener.utils';
import { DataLayerAssetsService } from './data-layer-assets.service';
import { DataLayerErrorService } from './data-layer-error.service';

@Injectable({ providedIn: 'root' })
export class DataLayerAnalyticsService {
  user: User = AUTHENTICATED_USER;
  analyticsData: DataLayerAnalytics = DataLayerPageLoad;
  pageLoadStart!: number;
  assets: AnalyticsSiteAssets[] = [];
  protected unsubscribe: Subject<void> = new Subject();

  constructor(
    private cookieService: CookieHelperService,
    private assetsService: DataLayerAssetsService,
    private errorService: DataLayerErrorService
  ) {
    setupBannerClickListeners();
  }

  setUserDataForAnalytics(): void {
    this.analyticsData = {
      ...this.analyticsData,
      user: this.user
    };
  }

  setUserDataForAnalyticsOnPageError(): void {
    this.analyticsData = {
      ...this.analyticsData,
      user: this.user,
      event: DATA_ANALYTICS.PAGE_ERROR,
      event_detail: DATA_ANALYTICS.PAGELOAD_ERROR
    };
  }

  getPageInfoForAnalytics(): void {
    const prefUI = this.cookieService.getCookieValue('prefUI') || '';
    this.analyticsData = getPageInfoDataForAnalytics(
      this.analyticsData,
      prefUI,
      this.pageLoadStart
    );
  }

  setDataForAnalyticsOnLoad(): void {
    this.pageLoadStart = Date.now();
    this.getPageInfoForAnalytics();
    this.setUserDataForAnalytics();
  }

  setDataForAnalyticsOnPageError(): void {
    this.getPageInfoForAnalytics();
    this.setUserDataForAnalyticsOnPageError();
    const errorData = this.errorService.getErrorDataForAnalytics(
      this.analyticsData
    );
    this.errorService.setErrorAnalytics(errorData);
  }

  setErrorData(
    error: HttpErrorResponse | ErrorDetails,
    message = '',
    eventName = '',
    type = ''
  ): void {
    this.setUserDataForAnalytics();
    const errorAnalytics = this.errorService.getErrorEventDataForAnalytics(
      this.analyticsData,
      error,
      message,
      eventName,
      type
    );
    this.errorService.setErrorAnalytics(errorAnalytics);
  }

  setModalClickAnalytics(data: string) {
    const analyticsData = getModalClickAttributes(this.analyticsData, data);
    setAdobeAnalytics(analyticsData);
  }

  setLinkClickAnalytics(
    data: SummaryAnalyticsEvent,
    assets: AnalyticsSiteAssets[]
  ): void {
    setLinkClickAnalyticsUtil(data, assets);
  }

  setQuickLinkAssets(quickLinks: OverviewInterfaces.QuickLinks): void {
    this.assetsService.setQuickLinkAssets(this.assets, quickLinks);
  }

  setOfferAssets(offers: OverviewInterfaces.Offers): void {
    this.assetsService.setOfferAssets(this.assets, offers);
  }

  setCardAssets(cardCarousel: OverviewInterfaces.CardCarousel): void {
    this.assetsService.setCardAssets(this.assets, cardCarousel);
  }

  setSummaryAssets(summary: Summary): void {
    this.assetsService.setSummaryAssets(this.assets, summary);
  }

  sendAnalyticsWithAssets(): void {
    this.assets = this.updateAssetsPosition(this.assets);
    updateSiteAssetsUtil(this.analyticsData, this.assets, this.pageLoadStart);
  }

  updateAssetsPosition(assets: AnalyticsSiteAssets[]): AnalyticsSiteAssets[] {
    const bannerAssets = assets.filter((asset) => asset.type === 'banner');
    const tileAssets = assets.filter((asset) => asset.type === 'tile');
    bannerAssets.forEach((asset, index) => {
      asset.position = index + 1 + ' of ' + bannerAssets.length;
    });

    tileAssets.forEach((asset, index) => {
      asset.position = index + 1 + ' of ' + tileAssets.length;
    });
    return [...bannerAssets, ...tileAssets];
  }
}
